import { IconUsers } from '../../../common/icons';
import AgentFace from '../../../common/components/agent-face/agent-face';
import { formatDateTime } from '../../../core/utils/date';
import { useAuthorNames } from '../../../global/contexts/lookups/lookups';

import '../styles/authorship.css';

// Who is behind something, said the same way on a plan, a tier and a task.
//
// ONE ROW PER QUESTION — who wrote it, who changed it last, who signed it — and
// each actor as what it is: a PERSON, or an AGENT ACTING FOR a person. The
// person is always named; when an agent did the typing, its face and name go
// first and the person it acted for follows. "Claude escribió esto" without the
// person would lose who answers for it.
//
// `rows`: [{ label, actor: { userId, tokenId, at } | null, hint?, empty? }]. A row whose
// actor is null says «sin registro» — the log has nothing, which is not the same
// as nobody.

export default function Authorship({ rows, readable = true, isLoading = false, title = 'Autoría' }) {
    const names = useAuthorNames();

    return (
        <section className="authorship" aria-label={title}>
            <h3 className="authorship__title">{title}</h3>

            {!readable
                ? (
                    <p className="authorship__note">
                        Hace falta <code>events:read</code> para ver quién la escribió: la autoría de
                        tiers y tareas vive en el registro de eventos.
                    </p>
                )
                : (
                    <dl className="authorship__rows">
                        {rows.map(row => (
                            <div key={row.label} className="authorship__row">
                                <dt>{row.label}</dt>
                                <dd>
                                    {isLoading && !row.actor
                                        ? <span className="authorship__muted">…</span>
                                        : <Actor actor={row.actor} names={names} hint={row.hint} empty={row.empty} />}
                                </dd>
                            </div>
                        ))}
                    </dl>
                )}
        </section>
    );
}

// `empty` is what a missing actor MEANS on that row: «todavía no» for an
// approval that has not happened, «sin registro» when the log has nothing.
function Actor({ actor, names, hint, empty = 'Sin registro' }) {
    if (!actor || (actor.userId == null && actor.tokenId == null)) {
        return <span className="authorship__muted">{empty}</span>;
    }

    const person = actor.userId != null ? names.userName(actor.userId) : null;
    const agent = actor.tokenId != null ? names.tokenName(actor.tokenId) : null;
    const kind = actor.tokenId != null ? names.tokenKind(actor.tokenId) : null;

    return (
        <span className="authorship__actor">
            {agent
                ? (
                    <>
                        <AgentFace kind={kind} size={18} title={agent} />
                        <span className="authorship__name">{agent}</span>
                        {person && <span className="authorship__for">por {person}</span>}
                    </>
                )
                : (
                    <>
                        <IconUsers />
                        <span className="authorship__name">{person}</span>
                    </>
                )}
            {actor.at && <span className="authorship__when">{formatDateTime(actor.at)}</span>}
            {hint && <span className="authorship__when">· {hint}</span>}
        </span>
    );
}
